'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { normalizeVoiceSearchQuery } from '@/lib/products/voice-search-normalizer';

type VoiceSearchBoxProps = {
  initialQuery: string;
};

export default function VoiceSearchBox({ initialQuery }: VoiceSearchBoxProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [text, setText] = useState(initialQuery);
  const [transcript, setTranscript] = useState(initialQuery);
  const [status, setStatus] = useState('Sẵn sàng');
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<DnTechSpeechRecognition | null>(null);

  const normalizedText = useMemo(() => normalizeVoiceSearchQuery(text), [text]);

  useEffect(() => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setIsSupported(false);
      setStatus('Trình duyệt chưa hỗ trợ');
      return;
    }

    const recognition = new Recognition();
    recognition.lang = 'vi-VN';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    recognition.onstart = () => {
      setIsListening(true);
      setStatus('Đang nghe');
    };
    recognition.onend = () => {
      setIsListening(false);
      setStatus('Sẵn sàng');
    };
    recognition.onerror = (event) => {
      setStatus(`Lỗi: ${event.error}`);
    };
    recognition.onresult = (event) => {
      const nextTranscript = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();

      setTranscript(nextTranscript);
      setText(nextTranscript);
    };

    recognitionRef.current = recognition;
    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  useEffect(() => {
    setText(initialQuery);
    setTranscript(initialQuery);
  }, [initialQuery]);

  function submitSearch(nextText = text) {
    const nextQuery = normalizeVoiceSearchQuery(nextText);
    const params = new URLSearchParams(searchParams.toString());
    params.delete('page');

    if (nextQuery) {
      params.set('q', nextQuery);
    } else {
      params.delete('q');
    }

    router.push(`/tim-kiem${params.toString() ? `?${params.toString()}` : ''}`);
  }

  function toggleVoice() {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    if (isListening) {
      recognition.stop();
      return;
    }

    recognition.start();
  }

  const quickTests = [
    'ba không sáu không ti',
    'vê gờ a a ti ích bốn không bảy không ti',
    'co i bảy mười hai ngàn bảy trăm ca',
    'co i năm mười hai ngàn bốn trăm ép',
  ];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 md:p-4">
      <form
        className="flex flex-col gap-3 md:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch();
        }}
      >
        <label className="sr-only" htmlFor="voice-search-input">
          Từ khóa tìm kiếm
        </label>
        <input
          id="voice-search-input"
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Nói hoặc nhập: vê gờ a rờ tê ích ba không sáu không ti"
          className="min-h-11 flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={toggleVoice}
            disabled={!isSupported}
            className={`min-h-11 rounded-lg px-4 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 ${
              isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isListening ? 'Dừng nghe' : 'Tìm bằng giọng nói'}
          </button>
          <button
            type="submit"
            className="min-h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary"
          >
            Tìm
          </button>
        </div>
      </form>

      <div className="mt-3 grid gap-2 text-xs text-gray-600 md:grid-cols-3">
        <div className="rounded border border-gray-200 bg-white p-2">
          <span className="font-semibold text-gray-700">Nhận diện: </span>
          {transcript || '-'}
        </div>
        <div className="rounded border border-gray-200 bg-white p-2">
          <span className="font-semibold text-gray-700">Chuẩn hóa: </span>
          {normalizedText || '-'}
        </div>
        <div className="rounded border border-gray-200 bg-white p-2">
          <span className="font-semibold text-gray-700">Trạng thái: </span>
          {status}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {quickTests.map((query) => (
          <button
            key={query}
            type="button"
            onClick={() => {
              setText(query);
              setTranscript(query);
              submitSearch(query);
            }}
            className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:border-primary hover:text-primary"
          >
            {normalizeVoiceSearchQuery(query)}
          </button>
        ))}
      </div>
    </div>
  );
}
